import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { ChatflowTemplateApiService } from '../services/chatflow-template-api.service';
import { ChatflowApiService } from '../services/chatflow-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { extractApiErrorMessage } from '../services/chatflow-error.util';
import {
  ChatflowResponse,
  ChatflowTemplateCreateRequest,
  ChatflowTemplateDetailResponse,
  ChatflowTemplateRequest,
  ChatflowTemplateResponse,
  ChatflowType
} from '../models/chatflow.model';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import {
  CHATFLOW_TEMPLATE_CATEGORIES,
  ChatflowCategoryOption,
  getChatflowCategoryLabel
} from '../services/chatflow-label.util';

/**
 * Submódulo de plantillas de chatflow: biblioteca de plantillas globales del sistema
 * + plantillas propias del negocio. Permite ver el detalle (definición base), editar y
 * eliminar SOLO las propias, y duplicar cualquiera (propia o global) creando una propia.
 * Las propias se crean aquí con "Crear plantilla" (flujo básico o copiando un chatflow) o
 * desde la lista de chatflows con "Guardar como plantilla".
 */
@Component({
  selector: 'app-chatflow-templates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    ModalComponent
  ],
  templateUrl: './chatflow-templates.component.html'
})
export class ChatflowTemplatesComponent implements OnInit {
  readonly categoriasPlantilla: ChatflowCategoryOption[] = CHATFLOW_TEMPLATE_CATEGORIES;

  templates: ChatflowTemplateResponse[] = [];
  loading = false;
  error = '';

  filtroBusqueda = '';
  deletingId: number | null = null;
  duplicatingId: number | null = null;

  // Modal "Ver detalle"
  showDetailModal = false;
  detailTemplate: ChatflowTemplateDetailResponse | null = null;
  detailLoading = false;
  detailError = '';

  // Modal "Editar plantilla" (solo propias)
  showEditModal = false;
  editTarget: ChatflowTemplateResponse | null = null;
  editNombre = '';
  editDescripcion = '';
  editCategoria = '';
  editThumbnail = '';
  editSortOrder: number | null = null;
  editActive = true;
  savingEdit = false;
  editError = '';

  // Modal "Crear plantilla" (Inc 5: antes llevaba al constructor de chatflows y el registro
  // terminaba en Chatflow, no en Plantillas)
  showCreateModal = false;
  createNombre = '';
  createDescripcion = '';
  createCategoria = 'GENERAL';
  createTipo: ChatflowType = 'BOT';
  /** '' = flujo básico; si no, id del chatflow cuyo flujo se copia. */
  createOrigenChatflowId = '';
  chatflowsOrigen: ChatflowResponse[] = [];
  savingCreate = false;
  createError = '';

  constructor(
    private templateApi: ChatflowTemplateApiService,
    private chatflowApi: ChatflowApiService,
    private authService: AuthService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    this.cargarTemplates();
  }

  // ── Crear plantilla ─────────────────────────────────────────────────────────

  crearPlantilla(): void {
    this.createNombre = '';
    this.createDescripcion = '';
    this.createCategoria = 'GENERAL';
    this.createTipo = 'BOT';
    this.createOrigenChatflowId = '';
    this.createError = '';
    this.savingCreate = false;
    this.showCreateModal = true;
    this.cargarChatflowsOrigen();
  }

  cerrarCrear(): void {
    if (this.savingCreate) return;
    this.showCreateModal = false;
  }

  private cargarChatflowsOrigen(): void {
    const businessId = this.authService.getBusinessId() || 1;
    this.chatflowApi.getChatflows({ businessId }).subscribe({
      next: (data) => (this.chatflowsOrigen = data ?? []),
      // Sin la lista solo se pierde la opción de copiar un flujo; el flujo básico sigue disponible.
      error: () => (this.chatflowsOrigen = [])
    });
  }

  guardarCreacion(): void {
    if (this.savingCreate) return;

    const nombre = this.createNombre.trim();
    if (!nombre) {
      this.createError = 'El nombre de la plantilla es obligatorio.';
      return;
    }

    const req: ChatflowTemplateCreateRequest = {
      name: nombre,
      description: this.createDescripcion.trim() || undefined,
      category: this.createCategoria || undefined,
      defaultChatflowType: this.createTipo,
      sourceChatflowId: this.createOrigenChatflowId ? Number(this.createOrigenChatflowId) : undefined
    };

    this.savingCreate = true;
    this.createError = '';

    this.templateApi.createTemplate(req).subscribe({
      next: (nueva) => {
        this.savingCreate = false;
        this.showCreateModal = false;
        this.toast.created('Plantilla', `Se creó la plantilla "${nueva.name}".`);
        this.cargarTemplates();
      },
      error: (err) => {
        // 400: chatflow origen sin nodos / categoría inválida; 404: chatflow origen de otra empresa.
        const msg = extractApiErrorMessage(err, 'No se pudo crear la plantilla.');
        this.savingCreate = false;
        this.createError = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cargarTemplates(): void {
    this.loading = true;
    this.error = '';

    this.templateApi.getTemplates().subscribe({
      next: (data) => {
        // Sistema primero (por sortOrder), luego las propias del negocio.
        this.templates = [...data].sort((a, b) => {
          const globalA = this.esGlobal(a) ? 0 : 1;
          const globalB = this.esGlobal(b) ? 0 : 1;
          if (globalA !== globalB) return globalA - globalB;
          return (a.sortOrder || 9999) - (b.sortOrder || 9999);
        });
        this.loading = false;
      },
      error: (err) => {
        this.templates = [];
        this.error = extractApiErrorMessage(err, 'No se pudo cargar la biblioteca de plantillas');
        this.loading = false;
      }
    });
  }

  /** Filtro client-side por nombre, clave o categoría. */
  get templatesFiltradas(): ChatflowTemplateResponse[] {
    const consulta = this.filtroBusqueda.trim().toLowerCase();
    if (!consulta) return this.templates;
    return this.templates.filter((template) =>
      template.name.toLowerCase().includes(consulta) ||
      template.templateKey.toLowerCase().includes(consulta) ||
      this.getCategoryLabel(template.category).toLowerCase().includes(consulta)
    );
  }

  /** Plantilla global del sistema: businessId null/ausente o marcada isSystem. */
  esGlobal(template: ChatflowTemplateResponse): boolean {
    return template.businessId == null || !!template.isSystem;
  }

  // ── Ver detalle ────────────────────────────────────────────────────────────

  verDetalle(template: ChatflowTemplateResponse): void {
    this.showDetailModal = true;
    this.detailTemplate = null;
    this.detailError = '';
    this.detailLoading = true;
    this.templateApi.getTemplateById(template.id).subscribe({
      next: (detail) => {
        this.detailTemplate = detail;
        this.detailLoading = false;
      },
      error: (err) => {
        this.detailError = extractApiErrorMessage(err, 'No se pudo cargar el detalle de la plantilla.');
        this.detailLoading = false;
      }
    });
  }

  cerrarDetalle(): void {
    this.showDetailModal = false;
    this.detailTemplate = null;
  }

  get detailDefinitionPreview(): string {
    const raw = this.detailTemplate?.definitionJson?.trim();
    if (!raw) return '';
    return raw.length > 1500 ? `${raw.slice(0, 1500)}...` : raw;
  }

  // ── Editar (solo propias) ───────────────────────────────────────────────────

  abrirEditar(template: ChatflowTemplateResponse): void {
    if (this.esGlobal(template)) return;
    this.editTarget = template;
    this.editNombre = template.name;
    this.editDescripcion = template.description || '';
    this.editCategoria = template.category || '';
    this.editThumbnail = template.thumbnailUrl || '';
    this.editSortOrder = template.sortOrder ?? null;
    this.editActive = template.isActive ?? true;
    this.editError = '';
    this.savingEdit = false;
    this.showEditModal = true;
  }

  cerrarEditar(): void {
    if (this.savingEdit) return;
    this.showEditModal = false;
    this.editTarget = null;
  }

  guardarEdicion(): void {
    if (!this.editTarget || this.savingEdit) return;

    const nombre = this.editNombre.trim();
    if (!nombre) {
      this.editError = 'El nombre de la plantilla es obligatorio.';
      return;
    }

    const req: ChatflowTemplateRequest = {
      name: nombre,
      description: this.editDescripcion.trim() || undefined,
      category: this.editCategoria || undefined,
      thumbnailUrl: this.editThumbnail.trim() || undefined,
      sortOrder: this.editSortOrder ?? undefined,
      isActive: this.editActive
    };

    this.savingEdit = true;
    this.editError = '';

    this.templateApi.updateTemplate(this.editTarget.id, req).subscribe({
      next: () => {
        this.savingEdit = false;
        this.showEditModal = false;
        this.editTarget = null;
        this.toast.updated('Plantilla');
        this.cargarTemplates();
      },
      error: (err) => {
        // 404: otra empresa; 409: plantilla de sistema/global no editable.
        const msg = extractApiErrorMessage(err, 'No se pudo actualizar la plantilla.');
        this.savingEdit = false;
        this.editError = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  // ── Duplicar (propias o globales) ───────────────────────────────────────────

  duplicarTemplate(template: ChatflowTemplateResponse): void {
    if (this.duplicatingId) return;
    this.duplicatingId = template.id;
    this.templateApi.duplicateTemplate(template.id).subscribe({
      next: (nueva) => {
        this.duplicatingId = null;
        this.toast.created('Plantilla', `Se duplicó "${template.name}" como "${nueva.name}".`);
        this.cargarTemplates();
      },
      error: (err) => {
        const msg = extractApiErrorMessage(err, 'No se pudo duplicar la plantilla.');
        this.duplicatingId = null;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  // ── Eliminar (solo propias) ─────────────────────────────────────────────────

  async eliminarTemplate(template: ChatflowTemplateResponse): Promise<void> {
    if (this.esGlobal(template)) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar plantilla',
      message: `¿Está seguro de eliminar la plantilla "${template.name}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar'
    });
    if (!ok) return;

    this.deletingId = template.id;
    this.templateApi.deleteTemplate(template.id).subscribe({
      next: () => {
        this.deletingId = null;
        this.toast.deleted('Plantilla');
        this.cargarTemplates();
      },
      error: (err) => {
        // 409: "Las plantillas globales del sistema no se pueden eliminar." / 404 / 403
        const msg = extractApiErrorMessage(err, 'No se pudo eliminar la plantilla.');
        this.deletingId = null;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  getCategoryLabel(category?: string): string {
    return getChatflowCategoryLabel(category);
  }

  getTypeLabel(type?: ChatflowType): string {
    if (!type) return '—';
    if (type === 'LIVE_CHAT') return 'Chat en vivo';
    if (type === 'AI_CHATBOT') return 'Chatbot IA';
    return 'Bot';
  }

  get totalSistema(): number {
    return this.templates.filter((template) => this.esGlobal(template)).length;
  }

  get totalPropias(): number {
    return this.templates.filter((template) => !this.esGlobal(template)).length;
  }
}
